import React, { useState, useEffect, useCallback } from 'react';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  MapPinIcon,
  DocumentIcon,
  BuildingOffice2Icon,
  HomeIcon,
  MapIcon,
  BuildingLibraryIcon,
  ScaleIcon,
  PhotoIcon,
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  CurrencyEuroIcon,
  CalendarDaysIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import ConfirmationModal from '../components/ConfirmationModal';
import apiClient from '../utils/apiClient';

interface ConfigData {
  general: {
    club_name: string;
    club_logo: string;
    street: string;
    house_number: string;
    postal_code: string;
    city: string;
    country: string;
    registration_number: string;
    court: string;
    full_address: string;
  };
}

interface MembershipContribution {
  year: number;
  adults: number;
  partner: number;
  youth: number;
}

interface CancellationPeriods {
  members: string | number;
  guests: number;
}

interface ConfirmModalState {
  visible: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onClose?: () => void;
  buttons?: {
    cancel: { text: string; onPress: () => void };
    confirm: { text: string; onPress: () => void };
  };
  button?: { text: string; onPress: () => void };
}

const AdminConfig: React.FC = () => {
  const [editMode, setEditMode] = useState<string | null>(null);
  const emptyGeneral: ConfigData = {
    general: {
      club_name: '', club_logo: '', street: '', house_number: '',
      postal_code: '', city: '', country: '', registration_number: '',
      court: '', full_address: ''
    }
  };
  const [formData, setFormData] = useState<ConfigData>(emptyGeneral);
  const [originalData, setOriginalData] = useState<ConfigData>(emptyGeneral);
  const [changedFields, setChangedFields] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState<{ [key: string]: boolean }>({});
  const [logoSrc, setLogoSrc] = useState<string | null>(null);

  const [contributions, setContributions] = useState<MembershipContribution[]>([]);
  const [cancellationPeriods, setCancellationPeriods] = useState<CancellationPeriods>({
    members: 'unbegrenzt',
    guests: 0,
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newYearData, setNewYearData] = useState({ year: '', adults: '', partner: '', youth: '' });
  const [editingYear, setEditingYear] = useState<number | null>(null);
  const [editYearData, setEditYearData] = useState({ year: '', adults: '', partner: '', youth: '' });
  const [editingCancellation, setEditingCancellation] = useState<string | null>(null);
  const [editCancellationData, setEditCancellationData] = useState({ members: '', guests: '' });

  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single',
  });

  useEffect(() => {
    fetchConfigData();
    loadLogo();
    fetchContributions();
    fetchCancellationPeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLogo = async () => {
    try {
      if (logoSrc && logoSrc.startsWith('blob:')) {
        window.URL.revokeObjectURL(logoSrc);
      }
      const response = await apiClient.get('/api/config/logo', { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      setLogoSrc(url);
    } catch (error) {
      console.error('Error loading logo:', error);
      setLogoSrc('/default_logo.png');
    }
  };

  useEffect(() => {
    return () => {
      if (logoSrc && logoSrc.startsWith('blob:')) {
        window.URL.revokeObjectURL(logoSrc);
      }
    };
  }, [logoSrc]);

  const fetchConfigData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/config');
      const { data } = response.data;
      const configData: ConfigData = {
        general: {
          club_name: data.club_name || '',
          club_logo: data.club_logo || '',
          street: data.street || '',
          house_number: data.house_number || '',
          postal_code: data.postal_code || '',
          city: data.city || '',
          country: data.country || '',
          registration_number: data.registration_number || '',
          court: data.court || '',
          full_address: [data.street, data.house_number, data.postal_code, data.city, data.country].filter(Boolean).join(', '),
        },
      };
      setFormData(configData);
      setOriginalData(configData);
      if (data.club_logo) loadLogo();
      else setLogoSrc('/default_logo.png');
    } catch {
      setConfirmModal({
        visible: true, type: 'error', title: 'Fehler',
        message: 'Fehler beim Laden der Daten.', variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchContributions = async () => {
    try {
      const response = await apiClient.get('/api/config/membership_contributions');
      if (response.data.success) setContributions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching contributions:', err);
    }
  };

  const fetchCancellationPeriods = async () => {
    try {
      const response = await apiClient.get('/api/config/cancellation_periods');
      if (response.data.success) setCancellationPeriods(response.data.data || { members: 'unbegrenzt', guests: 0 });
    } catch (err) {
      console.error('Error fetching cancellation periods:', err);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    const uploadData = new FormData();
    uploadData.append('logo', file);
    try {
      const response = await apiClient.post('/api/config/upload_logo', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = response.data;
      setFormData((prev) => ({ ...prev, general: { ...prev.general, club_logo: data.data.s3_key } }));
      loadLogo();
      setConfirmModal({
        visible: true, type: 'success', title: 'Erfolgreich',
        message: 'Logo erfolgreich hochgeladen!', variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
      });
    } catch {
      setConfirmModal({
        visible: true, type: 'error', title: 'Fehler',
        message: 'Fehler beim Hochladen.', variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoError = () => setLogoSrc('/default_logo.png');

  const handleInputChange = useCallback((section: string, name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [section]: { ...prev[section as keyof ConfigData], [name]: value } }));
    setChangedFields((prev: any) => ({ ...prev, [section]: { ...prev[section], [name]: true } }));
  }, []);

  const getChangedData = (section: string) => {
    const changed: any = {};
    const sectionData = formData[section as keyof ConfigData];
    for (const key in sectionData) {
      if (changedFields[section]?.[key] && sectionData[key as keyof typeof sectionData] !== originalData[section as keyof ConfigData][key as keyof typeof sectionData]) {
        changed[key] = sectionData[key as keyof typeof sectionData];
      }
    }
    return changed;
  };

  const validateSection = (section: string) => {
    const errors: string[] = [];
    const data = formData[section as keyof ConfigData];
    if (section === 'general') {
      if (!data.club_name?.trim()) errors.push('Vereinsname ist erforderlich.');
    }
    return errors;
  };

  const handleSubmit = async (section: string, e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateSection(section);
    if (errors.length > 0) {
      setConfirmModal({
        visible: true, type: 'error', title: 'Fehler',
        message: errors.join('\n'), variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
      });
      return;
    }
    setSectionLoading((prev) => ({ ...prev, [section]: true }));
    try {
      const changedData = getChangedData(section);
      if (Object.keys(changedData).length === 0) {
        setConfirmModal({
          visible: true, type: 'info', title: 'Information',
          message: 'Keine Änderungen zum Speichern.', variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        });
        setEditMode(null);
        setSectionLoading((prev) => ({ ...prev, [section]: false }));
        return;
      }
      const response = await apiClient.patch('/api/config', { [section]: changedData });
      const data = response.data;
      if (data.success) {
        setConfirmModal({
          visible: true, type: 'success', title: 'Erfolgreich',
          message: `${section.charAt(0).toUpperCase() + section.slice(1)} erfolgreich aktualisiert!`,
          variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        });
        setOriginalData((prev) => ({ ...prev, [section]: { ...prev[section as keyof ConfigData], ...changedData } }));
        setChangedFields((prev: any) => ({ ...prev, [section]: {} }));
        setEditMode(null);
      } else {
        setConfirmModal({
          visible: true, type: 'error', title: 'Fehler',
          message: data.message || `Fehler beim Speichern der Gruppe ${section}.`, variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        });
      }
    } catch {
      setConfirmModal({
        visible: true, type: 'error', title: 'Fehler',
        message: 'Netzwerkfehler beim Speichern.', variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
      });
    } finally {
      setSectionLoading((prev) => ({ ...prev, [section]: false }));
    }
  };

  const handleCancel = (section: string) => {
    setFormData((prev) => ({ ...prev, [section]: { ...originalData[section as keyof ConfigData] } }));
    setChangedFields((prev: any) => ({ ...prev, [section]: {} }));
    setEditMode(null);
  };

  const handleAddYear = async () => {
    if (!newYearData.year || !newYearData.adults || !newYearData.partner || !newYearData.youth) {
      setConfirmModal({
        visible: true, type: 'error', title: 'Fehler',
        message: 'Bitte füllen Sie alle Felder aus.', variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
      });
      return;
    }
    try {
      const response = await apiClient.post('/api/config/membership_contributions', {
        year: parseInt(newYearData.year),
        adults: parseFloat(newYearData.adults),
        partner: parseFloat(newYearData.partner),
        youth: parseFloat(newYearData.youth),
      });
      if (response.data.success) {
        await fetchContributions();
        setNewYearData({ year: '', adults: '', partner: '', youth: '' });
        setShowAddForm(false);
        setConfirmModal({
          visible: true, type: 'success', title: 'Erfolgreich',
          message: 'Jahr erfolgreich hinzugefügt.', variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        });
      } else {
        setConfirmModal({
          visible: true, type: 'error', title: 'Fehler',
          message: response.data.message || 'Fehler beim Hinzufügen des Jahres.', variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Fehler beim Hinzufügen des Jahres.';
      setConfirmModal({
        visible: true, type: 'error', title: 'Fehler', message: errorMessage, variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
      });
    }
  };

  const handleEditYear = (entry: MembershipContribution) => {
    setEditingYear(entry.year);
    setEditYearData({
      year: entry.year.toString(),
      adults: entry.adults.toString(),
      partner: entry.partner.toString(),
      youth: entry.youth.toString(),
    });
  };

  const handleSaveEditYear = async () => {
    try {
      const response = await apiClient.put(`/api/config/membership_contributions/${editingYear}`, {
        adults: parseFloat(editYearData.adults),
        partner: parseFloat(editYearData.partner),
        youth: parseFloat(editYearData.youth),
      });
      if (response.data.success) {
        await fetchContributions();
        setEditingYear(null);
        setEditYearData({ year: '', adults: '', partner: '', youth: '' });
        setConfirmModal({
          visible: true, type: 'success', title: 'Erfolgreich',
          message: 'Jahr erfolgreich aktualisiert.', variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        });
      } else {
        setConfirmModal({
          visible: true, type: 'error', title: 'Fehler',
          message: response.data.message || 'Fehler beim Aktualisieren des Jahres.', variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        });
      }
    } catch {
      setConfirmModal({
        visible: true, type: 'error', title: 'Fehler',
        message: 'Fehler beim Aktualisieren des Jahres.', variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
      });
    }
  };

  const handleDeleteYear = async (year: number) => {
    setConfirmModal({
      visible: true, type: 'warning', title: 'Löschen bestätigen',
      message: `Möchten Sie das Jahr ${year} wirklich löschen?`, variant: 'two',
      buttons: {
        cancel: { text: 'Abbrechen', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        confirm: {
          text: 'Löschen',
          onPress: async () => {
            try {
              const response = await apiClient.delete(`/api/config/membership_contributions/${year}`);
              if (response.data.success) {
                await fetchContributions();
                setConfirmModal({
                  visible: true, type: 'success', title: 'Erfolgreich',
                  message: 'Jahr erfolgreich gelöscht.', variant: 'single',
                  button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
                });
              } else {
                setConfirmModal({
                  visible: true, type: 'error', title: 'Fehler',
                  message: response.data.message || 'Fehler beim Löschen des Jahres.', variant: 'single',
                  button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
                });
              }
            } catch {
              setConfirmModal({
                visible: true, type: 'error', title: 'Fehler',
                message: 'Fehler beim Löschen des Jahres.', variant: 'single',
                button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
              });
            }
          },
        },
      },
    });
  };

  const handleEditCancellation = (type: string) => {
    setEditingCancellation(type);
    setEditCancellationData({
      members: cancellationPeriods.members === 'unbegrenzt' ? '' : cancellationPeriods.members.toString(),
      guests: cancellationPeriods.guests.toString(),
    });
  };

  const handleSaveCancellation = async () => {
    try {
      const response = await apiClient.put('/api/config/cancellation_periods', {
        members: editCancellationData.members === '' ? 'unbegrenzt' : parseInt(editCancellationData.members),
        guests: parseInt(editCancellationData.guests),
      });
      if (response.data.success) {
        await fetchCancellationPeriods();
        setEditingCancellation(null);
        setEditCancellationData({ members: '', guests: '' });
        setConfirmModal({
          visible: true, type: 'success', title: 'Erfolgreich',
          message: 'Stornierungsfristen erfolgreich aktualisiert.', variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        });
      } else {
        setConfirmModal({
          visible: true, type: 'error', title: 'Fehler',
          message: response.data.message || 'Fehler beim Aktualisieren der Stornierungsfristen.', variant: 'single',
          button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
        });
      }
    } catch {
      setConfirmModal({
        visible: true, type: 'error', title: 'Fehler',
        message: 'Fehler beim Aktualisieren der Stornierungsfristen.', variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal((prev) => ({ ...prev, visible: false })) },
      });
    }
  };

  const cardClass = 'bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700';
  const subTileClass = 'bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700';
  const inputClass = 'bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700';
  const inputStyleBase = { colorScheme: 'light dark' as const };
  const primaryBtn = 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _secondaryBtn = 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-white/10 font-semibold rounded-xl transition';

  if (loading) {
    return (
      <div className="min-h-screen font-sans text-gray-900 dark:text-slate-200" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto py-2 sm:py-4 px-4 sm:px-6 space-y-6">
          <div className={`rounded-3xl p-6 ${cardClass}`}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl animate-pulse bg-gray-100 dark:bg-white/5" />
              <div className="space-y-2 flex-1">
                <div className="h-6 w-48 rounded animate-pulse bg-gray-100 dark:bg-white/5" />
                <div className="h-4 w-32 rounded animate-pulse bg-gray-100 dark:bg-white/5" />
              </div>
            </div>
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className={`rounded-3xl p-6 space-y-3 ${cardClass}`}>
              <div className="h-5 w-1/3 rounded animate-pulse bg-gray-100 dark:bg-white/5" />
              <div className="h-10 rounded-xl animate-pulse bg-gray-100 dark:bg-white/5" />
              <div className="h-10 rounded-xl animate-pulse bg-gray-100 dark:bg-white/5" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const renderSection = (
    section: string,
    title: string,
    icon: React.ReactNode,
    fields: Array<{ name: string; label: string; type?: string; required?: boolean; placeholder?: string; icon?: React.ReactNode }>,
  ) => (
    <section className={`rounded-3xl p-6 ${cardClass}`}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2.5">
          <span className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-600 dark:text-amber-400">
            {icon}
          </span>
          {title}
        </h2>
        <div className="flex gap-2 w-full sm:w-auto">
          {editMode !== section ? (
            <button
              onClick={() => setEditMode(section)}
              disabled={sectionLoading[section]}
              className={`${primaryBtn} w-full sm:w-auto px-4 py-2.5 flex items-center justify-center gap-2`}
            >
              <PencilIcon className="h-4 w-4" /> Bearbeiten
            </button>
          ) : (
            <>
              <button
                onClick={(e) => handleSubmit(section, e)}
                disabled={sectionLoading[section]}
                className={`${primaryBtn} w-full sm:w-auto px-4 py-2.5 flex items-center justify-center gap-2`}
              >
                {sectionLoading[section] ? (
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z" />
                  </svg>
                ) : (
                  <CheckIcon className="h-4 w-4" />
                )}
                Speichern
              </button>
              <button
                onClick={() => handleCancel(section)}
                disabled={sectionLoading[section]}
                className="w-full sm:w-auto px-4 py-2.5 bg-rose-500/15 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/25 rounded-xl font-bold transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <XMarkIcon className="h-4 w-4" /> Abbrechen
              </button>
            </>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-600 dark:text-slate-400 mb-6 pl-3 py-1 border-l-4 border-amber-500/50">
        Konfigurieren Sie die allgemeinen Informationen Ihres Vereins.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        {fields.map(({ name, label, type = 'text', required, placeholder, icon: fieldIcon }) => (
          <div key={`${section}-${name}`}>
            <label htmlFor={`${section}-${name}`} className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-1.5 flex items-center gap-2">
              {fieldIcon && <span className="text-gray-500 dark:text-slate-500">{fieldIcon}</span>}
              {label}
            </label>
            {type === 'logo' ? (
              <div className="flex items-center gap-4">
                <img
                  src={logoSrc || '/default_logo.png'}
                  alt="Vereinslogo"
                  className="w-16 h-16 object-contain rounded-xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-slate-700"
                  onError={handleLogoError}
                />
                {editMode === section && (
                  <label className={`${primaryBtn} px-4 py-2.5 cursor-pointer flex items-center gap-2`} aria-label="Logo hochladen">
                    <PhotoIcon className="h-4 w-4" />
                    Logo ändern
                    <input type="file" accept="image/*" hidden onChange={handleLogoUpload} disabled={sectionLoading[section]} />
                  </label>
                )}
              </div>
            ) : (
              <input
                id={`${section}-${name}`}
                type={type}
                name={name}
                value={formData[section as keyof ConfigData][name as keyof typeof formData.general] || ''}
                onChange={(e) => handleInputChange(section, name, e.target.value)}
                disabled={editMode !== section || sectionLoading[section]}
                required={required}
                className={`w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none transition placeholder:text-gray-500 dark:placeholder:text-slate-500 placeholder:font-normal disabled:opacity-60 disabled:cursor-not-allowed ${
                  changedFields[section]?.[name]
                    ? 'bg-gray-50 dark:bg-slate-900/60 border border-amber-500/50'
                    : inputClass
                }`}
                style={inputStyleBase}
                placeholder={placeholder}
                aria-label={label}
              />
            )}
            {editMode === section && required && !formData[section as keyof ConfigData][name as keyof typeof formData.general]?.trim() && (
              <p className="text-rose-700 dark:text-rose-300 text-xs mt-1">Pflichtfeld.</p>
            )}
          </div>
        ))}
        {section === 'general' && (
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-1.5 flex items-center gap-2">
              <MapPinIcon className="h-4 w-4 text-gray-500 dark:text-slate-500" />
              Vollständige Adresse
            </label>
            <input
              type="text"
              value={formData[section as keyof ConfigData].full_address || ''}
              disabled
              className="w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-500 dark:text-slate-400 outline-none cursor-not-allowed bg-gray-100 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-800"
              aria-label="Vollständige Adresse (automatisch generiert)"
            />
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className="min-h-screen font-sans text-gray-900 dark:text-slate-200" style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}>
      <div className="max-w-7xl mx-auto py-2 sm:py-4">
        {/* Hero */}
        <section className="pt-2 pb-6 px-4 sm:px-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600/80 dark:text-amber-400/80 font-semibold mb-3">
                ADMIN · Vereinseinstellungen
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
                Wie alles<br /><span className="text-amber-600 dark:text-amber-400">tickt.</span>
              </h1>
              <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
                Vereinsname, Anschrift, Logo, Beiträge und Stornierungsfristen — die Schaltzentrale, in der ihr alle
                <span className="font-bold text-amber-700 dark:text-amber-300"> harten Stammdaten</span> des Vereins verwaltet.
              </p>
        </section>

        <div className="px-4 sm:px-6 space-y-6">
          {renderSection('general', 'Allgemeine Einstellungen', <MapPinIcon className="h-5 w-5" />, [
            { name: 'club_name', label: 'Vereinsname', required: true, placeholder: 'Vereinsname', icon: <BuildingOffice2Icon className="h-4 w-4" /> },
            { name: 'club_logo', label: 'Vereinslogo', type: 'logo', placeholder: '' },
            { name: 'registration_number', label: 'Vereins Registrierungsnummer', required: false, placeholder: 'VR 12345', icon: <DocumentIcon className="h-4 w-4" /> },
            { name: 'court', label: 'Gericht', placeholder: 'Amtsgericht Musterstadt', icon: <ScaleIcon className="h-4 w-4" /> },
            { name: 'street', label: 'Straße', placeholder: 'Musterstraße', icon: <MapIcon className="h-4 w-4" /> },
            { name: 'house_number', label: 'Hausnummer', placeholder: '123', icon: <HomeIcon className="h-4 w-4" /> },
            { name: 'postal_code', label: 'PLZ', placeholder: '12345', icon: <MapPinIcon className="h-4 w-4" /> },
            { name: 'city', label: 'Ort', placeholder: 'Musterstadt', icon: <BuildingLibraryIcon className="h-4 w-4" /> },
            { name: 'country', label: 'Land', placeholder: 'Deutschland', icon: <MapIcon className="h-4 w-4" /> },
          ])}

          {/* Mitgliedsbeiträge */}
          <section className={`rounded-3xl p-6 ${cardClass}`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
              <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                  <UserGroupIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </span>
                Mitgliedsbeiträge pro Jahr
              </h2>
              <button onClick={() => setShowAddForm(!showAddForm)} className={`${primaryBtn} w-full sm:w-auto px-4 py-2.5 flex items-center justify-center gap-2`}>
                <PlusIcon className="h-4 w-4" />
                {showAddForm ? 'Abbrechen' : 'Neues Jahr hinzufügen'}
              </button>
            </div>

            {showAddForm && (
              <div className={`rounded-2xl p-6 mb-4 ${subTileClass}`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[
                    { key: 'year', label: 'Jahr', icon: <CalendarDaysIcon className="h-4 w-4 text-gray-500 dark:text-slate-500" />, placeholder: 'z.B. 2027' },
                    { key: 'adults', label: 'Erwachsene', icon: <CurrencyEuroIcon className="h-4 w-4 text-gray-500 dark:text-slate-500" />, placeholder: '€' },
                    { key: 'partner', label: 'Partner', icon: <CurrencyEuroIcon className="h-4 w-4 text-gray-500 dark:text-slate-500" />, placeholder: '€' },
                    { key: 'youth', label: 'Jugendliche', icon: <CurrencyEuroIcon className="h-4 w-4 text-gray-500 dark:text-slate-500" />, placeholder: '€' },
                  ].map(({ key, label, icon, placeholder }) => (
                    <div key={key}>
                      <label className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-1.5 flex items-center gap-2">
                        {icon}
                        {label}
                      </label>
                      <input
                        type="number"
                        placeholder={placeholder}
                        value={(newYearData as any)[key]}
                        onChange={(e) => setNewYearData({ ...newYearData, [key]: e.target.value })}
                        className={`w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none placeholder:text-gray-500 dark:placeholder:text-slate-500 placeholder:font-normal ${inputClass}`}
                        style={inputStyleBase}
                      />
                    </div>
                  ))}
                </div>
                <button onClick={handleAddYear} className={`${primaryBtn} mt-4 px-4 py-2.5`}>
                  Hinzufügen
                </button>
              </div>
            )}

            <div className={`rounded-2xl overflow-x-auto ${subTileClass}`}>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-amber-500/10 border-b border-gray-200 dark:border-slate-700">
                    <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-300">Jahr</th>
                    <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-300">Erwachsene (€)</th>
                    <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-300">Partner (€)</th>
                    <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-300">Jugendliche (€)</th>
                    <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-300">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {contributions.map((entry, index) => (
                    <tr key={index} className="border-b border-gray-200 dark:border-slate-800">
                      {editingYear === entry.year ? (
                        <>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              value={editYearData.year}
                              onChange={(e) => setEditYearData({ ...editYearData, year: e.target.value })}
                              className={`px-2 py-2 rounded-lg w-full text-sm font-bold text-gray-900 dark:text-white outline-none ${inputClass}`}
                              style={inputStyleBase}
                              disabled
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              value={editYearData.adults}
                              onChange={(e) => setEditYearData({ ...editYearData, adults: e.target.value })}
                              className={`px-2 py-2 rounded-lg w-full text-sm font-bold text-gray-900 dark:text-white outline-none ${inputClass}`}
                              style={inputStyleBase}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              value={editYearData.partner}
                              onChange={(e) => setEditYearData({ ...editYearData, partner: e.target.value })}
                              className={`px-2 py-2 rounded-lg w-full text-sm font-bold text-gray-900 dark:text-white outline-none ${inputClass}`}
                              style={inputStyleBase}
                            />
                          </td>
                          <td className="py-3 px-4">
                            <input
                              type="number"
                              value={editYearData.youth}
                              onChange={(e) => setEditYearData({ ...editYearData, youth: e.target.value })}
                              className={`px-2 py-2 rounded-lg w-full text-sm font-bold text-gray-900 dark:text-white outline-none ${inputClass}`}
                              style={inputStyleBase}
                            />
                          </td>
                          <td className="py-3 px-4 flex gap-2">
                            <button onClick={handleSaveEditYear} className="text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200">
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button onClick={() => setEditingYear(null)} className="text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200">
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-3 px-4 text-gray-900 dark:text-white font-bold tabular-nums">{entry.year}</td>
                          <td className="py-3 px-4 text-gray-800 dark:text-slate-200 tabular-nums">{Number(entry.adults).toFixed(2)}</td>
                          <td className="py-3 px-4 text-gray-800 dark:text-slate-200 tabular-nums">{Number(entry.partner).toFixed(2)}</td>
                          <td className="py-3 px-4 text-gray-800 dark:text-slate-200 tabular-nums">{Number(entry.youth).toFixed(2)}</td>
                          <td className="py-3 px-4 flex gap-2">
                            <button onClick={() => handleEditYear(entry)} className="text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200">
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button onClick={() => handleDeleteYear(entry.year)} className="text-rose-700 dark:text-rose-300 hover:text-rose-800 dark:hover:text-rose-200">
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Stornierungsfristen */}
          <section className={`rounded-3xl p-6 ${cardClass}`}>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2.5 mb-6">
              <span className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </span>
              Stornierungsfristen
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={`rounded-2xl p-4 ${subTileClass}`}>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">Mitglieder</h3>
                {editingCancellation === 'members' ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="unbegrenzt oder Anzahl Stunden"
                      value={editCancellationData.members}
                      onChange={(e) => setEditCancellationData({ ...editCancellationData, members: e.target.value })}
                      className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none placeholder:text-gray-500 dark:placeholder:text-slate-500 placeholder:font-normal ${inputClass}`}
                      style={inputStyleBase}
                    />
                    <button onClick={handleSaveCancellation} className="text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200">
                      <CheckIcon className="h-5 w-5" />
                    </button>
                    <button onClick={() => setEditingCancellation(null)} className="text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-gray-700 dark:text-slate-300 font-bold tabular-nums">
                      {cancellationPeriods.members === 'unbegrenzt' ? 'Unbegrenzt' : `${cancellationPeriods.members} Stunden`}
                    </p>
                    <button onClick={() => handleEditCancellation('members')} className="text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200">
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
              <div className={`rounded-2xl p-4 ${subTileClass}`}>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">Gäste</h3>
                {editingCancellation === 'guests' ? (
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Anzahl Stunden"
                      value={editCancellationData.guests}
                      onChange={(e) => setEditCancellationData({ ...editCancellationData, guests: e.target.value })}
                      className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none placeholder:text-gray-500 dark:placeholder:text-slate-500 placeholder:font-normal ${inputClass}`}
                      style={inputStyleBase}
                    />
                    <button onClick={handleSaveCancellation} className="text-emerald-700 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-200">
                      <CheckIcon className="h-5 w-5" />
                    </button>
                    <button onClick={() => setEditingCancellation(null)} className="text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-gray-700 dark:text-slate-300 font-bold tabular-nums">{cancellationPeriods.guests} Stunden</p>
                    <button onClick={() => handleEditCancellation('guests')} className="text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200">
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={confirmModal.onClose}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={confirmModal.buttons}
        button={confirmModal.button}
      />
    </div>
  );
};

export default AdminConfig;
